import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BasePasswordForm } from '@/components/core';
import { useResetPassword } from '@/api-client/use-reset-password';
import { resetPasswordFormDefaultValue, getResetPasswordFormValidationSchema } from './utils';

/**
 * ResetPasswordForm Component
 *
 * A wrapper component that handles the reset password functionality by connecting
 * the reset password API mutation with a base password form component.
 *
 * Features:
 * - Handles reset password API integration
 * - Manages CAPTCHA validation state
 * - Passes loading state to the base form
 * - Passes the reset code from URL to the API
 *
 * @param {Object} props - Component props
 * @param {string} props.code - Verification code from the reset password link
 *
 * @example
 * // Basic usage with reset code from URL params
 * const [searchParams] = useSearchParams();
 * <ResetPasswordForm code={code || ''} />
 *
 * // With explicit reset code
 * <ResetPasswordForm code="abc123def456" />
 */

export const ResetpasswordForm = ({ code }: Readonly<{ code: string }>) => {
  const { t } = useTranslation();
  const { ResetPassword: mutateAsync, isPending } = useResetPassword();

  const [isCaptchaValid, setIsCaptchaValid] = useState(false);

  const captchaEnabled = (import.meta.env.VITE_CAPTCHA_SITE_KEY || '') !== '';

  const handleSubmit = async (
    password: string,
    code: string,
    captchaToken?: string
  ) => {
    if (captchaEnabled && !captchaToken) {
      return;
    }
    
    await mutateAsync({
      password,
      code,
      captchaCode: captchaToken ?? '',
      logoutFromAllDevices: false,
    });
  };

  const handleCaptchaValidation = (isValid: boolean) => {
    setIsCaptchaValid(isValid);
  };

  return (
    <BasePasswordForm
      code={code}
      onSubmit={handleSubmit}
      validationSchema={getResetPasswordFormValidationSchema(t)}
      defaultValues={resetPasswordFormDefaultValue}
      isPending={isPending}
      isCaptchaValid={isCaptchaValid}
      onCaptchaValidation={handleCaptchaValidation}
    />
  );
};
